/**
 * Uniform spatial hash for neighbor queries (SIM-07 / DESIGNS-TRACK-3D).
 * 2D: 3×3 stencil. 3D: 3×3×3 stencil when dim ≥ 3.
 */

export class SpatialHash2D {
  private cellSize: number;
  private inv: number;
  private cells = new Map<number, number[]>();
  private dimMode: 2 | 3 = 2;

  constructor(cellSize = 0.35) {
    this.cellSize = Math.max(1e-6, cellSize);
    this.inv = 1 / this.cellSize;
  }

  get radius(): number {
    return this.cellSize;
  }

  clear(): void {
    this.cells.clear();
  }

  setCellSize(cellSize: number): void {
    this.cellSize = Math.max(1e-6, cellSize);
    this.inv = 1 / this.cellSize;
    this.cells.clear();
  }

  rebuild(positions: Float64Array, dim: number, n: number): void {
    this.cells.clear();
    this.dimMode = dim >= 3 ? 3 : 2;
    for (let i = 0; i < n; i++) {
      const base = i * dim;
      const x = positions[base]!;
      const y = positions[base + 1]!;
      const z = dim >= 3 ? positions[base + 2]! : 0;
      const key = this.keyAt(x, y, z);
      let bucket = this.cells.get(key);
      if (!bucket) {
        bucket = [];
        this.cells.set(key, bucket);
      }
      bucket.push(i);
    }
  }

  private keyAt(x: number, y: number, z: number): number {
    const cx = Math.floor(x * this.inv);
    const cy = Math.floor(y * this.inv);
    const cz = Math.floor(z * this.inv);
    // 10 bits each signed-ish pack (range ±512 cells)
    return (
      ((cx + 512) & 0x3ff) |
      (((cy + 512) & 0x3ff) << 10) |
      (((cz + 512) & 0x3ff) << 20)
    );
  }

  private cellCoords(
    x: number,
    y: number,
    z: number,
  ): [number, number, number] {
    return [
      Math.floor(x * this.inv),
      Math.floor(y * this.inv),
      Math.floor(z * this.inv),
    ];
  }

  forNeighbors(
    i: number,
    positions: Float64Array,
    dim: number,
    r2: number,
    fn: (j: number, d2: number) => void,
  ): void {
    const base = i * dim;
    const ix = positions[base]!;
    const iy = positions[base + 1]!;
    const iz = dim >= 3 ? positions[base + 2]! : 0;
    const [cx, cy, cz] = this.cellCoords(ix, iy, iz);
    const zLo = this.dimMode === 3 ? -1 : 0;
    const zHi = this.dimMode === 3 ? 1 : 0;

    for (let dz = zLo; dz <= zHi; dz++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const key = this.keyAt(
            (cx + dx) * this.cellSize,
            (cy + dy) * this.cellSize,
            (cz + dz) * this.cellSize,
          );
          // keyAt floors again — better pack directly:
          const k =
            ((cx + dx + 512) & 0x3ff) |
            (((cy + dy + 512) & 0x3ff) << 10) |
            (((cz + dz + 512) & 0x3ff) << 20);
          void key;
          const bucket = this.cells.get(k);
          if (!bucket) continue;
          for (let t = 0; t < bucket.length; t++) {
            const j = bucket[t]!;
            if (j === i) continue;
            const jb = j * dim;
            const ddx = ix - positions[jb]!;
            const ddy = iy - positions[jb + 1]!;
            const ddz = dim >= 3 ? iz - positions[jb + 2]! : 0;
            const d2 = ddx * ddx + ddy * ddy + ddz * ddz;
            if (d2 < r2 && d2 > 0) fn(j, d2);
          }
        }
      }
    }
  }

  queryIndices(
    i: number,
    positions: Float64Array,
    dim: number,
    radius: number,
    out: number[],
  ): void {
    out.length = 0;
    const r2 = radius * radius;
    this.forNeighbors(i, positions, dim, r2, (j) => {
      out.push(j);
    });
  }
}

export function bruteNeighbors(
  i: number,
  positions: Float64Array,
  dim: number,
  n: number,
  radius: number,
): number[] {
  const r2 = radius * radius;
  const base = i * dim;
  const ix = positions[base]!;
  const iy = positions[base + 1]!;
  const iz = dim >= 3 ? positions[base + 2]! : 0;
  const out: number[] = [];
  for (let j = 0; j < n; j++) {
    if (j === i) continue;
    const jb = j * dim;
    const dx = ix - positions[jb]!;
    const dy = iy - positions[jb + 1]!;
    const dz = dim >= 3 ? iz - positions[jb + 2]! : 0;
    if (dx * dx + dy * dy + dz * dz < r2) out.push(j);
  }
  return out.sort((a, b) => a - b);
}
